/**
 * arloSeasons.js - season-slug helpers for Arlo recommendation surfaces.
 *
 * Arlo's similarity/rails index is season-granular: a series surfaces as
 * per-season catalog slugs (`dragnet-season-8`, `dick-tracy-season-1`) rather
 * than the parent-series slug. On ChillFree we only ever want the parent-series
 * tile, so we collapse those season slugs back to their parent and dedupe.
 *
 * This is an interim client guard. The durable fix is server-side in Arlo —
 * chill-backend #544 did the exclude-family + collapse-to-series for
 * `/v1/embed/similar`; a sibling issue tracks the same collapse for
 * `/v1/embed/rails`. Once rails return series-level ids, parentSlugOf() stops
 * matching and this becomes a no-op (the same way the #544 client `_id` filter
 * went dormant). Season channels carry no parent-show foreign key, so the slug
 * convention is the only client-side signal available (per #544's own analysis).
 */

// Trailing "-season-<n>" on a catalog slug, case-insensitive. Anchored to the
// end so a title like "four-seasons" or "the-good-season" is left untouched;
// only a "-season-<digits>" suffix is treated as a season.
const SEASON_SLUG_RE = /-season-\d+$/i;

/**
 * The parent-series slug for a season slug, or null when `contentId` is not a
 * season slug — i.e. a movie, a series, an ObjectId, or a non-string. Callers
 * treat a non-null result as "this item is a season; render its parent instead."
 * @param {*} contentId
 * @returns {string|null}
 */
export const parentSlugOf = contentId =>
  typeof contentId === 'string' && SEASON_SLUG_RE.test(contentId)
    ? contentId.replace(SEASON_SLUG_RE, '')
    : null;
